import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IdentifyPickField } from "@/components/IdentifyPickField";
import { toast } from "sonner";
import { Plus, Hash, ScanLine, Sparkles, Link2, AlertTriangle, BookCheck } from "lucide-react";
import { ScanTextInput } from "@/components/ScanTextInput";
import { PartNumbersGrouped } from "@/components/PartNumbersGrouped";

const OWNERS = [["unknown", "Unknown"], ["truck_oem", "Truck OEM (Freightliner/KW/Pete…)"], ["component_maker", "Component maker (Cummins/Bosch/Eaton…)"], ["interchange", "Interchange"], ["casting", "Casting / family"], ["tag_sticker", "Tag / sticker"]];
const SOURCES = [
  // "OEM verified" is a SOURCE — a number matched to an OEM catalogue — and it is deliberately no
  // longer dressed in the shield + green that now mean one thing only: a PERSON checked this
  // number against the physical tag. His rule, 2026-10-08: *"'OEM verified' becomes a stamp a
  // person creates."* The stamp is the emerald badge on the row; this is just where it came from.
  ["oem_verified", "From an OEM source", BookCheck, "bg-blue-700"],
  ["from_tag", "From tag / sticker", ScanLine, "bg-blue-600"],
  ["manual", "Entered by staff", Hash, "bg-slate-500"],
  ["ai_found_on_source", "AI-found on source", Link2, "bg-amber-500"],
  ["ai_memory", "AI-suggested (memory)", Sparkles, "bg-[#B01E28]"],
];

// Slice A editor — a part can hold MULTIPLE numbers, each qualified with owner + brand +
// source (+ OCR confidence). Every number is searchable; the source label tells the truth.
export function PartNumbersEditor({ partId, part, numbers = [], needsReconcile = false, onChange, onReload, brandOptions = [], canAddBrand = false, onAddBrand }) {
  const [list, setList] = useState(numbers);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ value: "", owner: "unknown", owner_name: "", brand: "", source: "manual", ocr_confidence: "", source_links: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { setList(numbers); }, [numbers]);

  const refresh = (sp) => { setList(sp.numbers || []); onChange?.(sp); };

  const add = async () => {
    if (!form.value.trim()) { toast.error("Enter the number."); return; }
    if (form.source === "ai_found_on_source" && !form.source_links.trim()) { toast.error("An 'AI-found on source' number needs a source link."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${partId}/numbers`, {
        ...form, read_by: "person",
        source_links: form.source_links.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Number added");
      refresh(data); setAdding(false);
      setForm({ value: "", owner: "unknown", owner_name: "", brand: "", source: "manual", ocr_confidence: "", source_links: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (nid) => {
    setBusy(true);
    try { const { data } = await api.delete(`/parts/${partId}/numbers/${nid}`); toast.success("Number removed"); refresh(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // A PERSON stamps a number as checked. Until this is done an AI-read number is fully
  // searchable but can never BE the part's identity — his rule: "PART NUMBER and SERIAL NUMBER...
  // must be 100% VERIFIED by a person. The AI can suggest them, but they're never saved as final
  // on the AI's word alone."

  return (
    <div data-testid="part-numbers-editor" className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" />Part numbers</label>
        {needsReconcile && <Badge data-testid="numbers-reconcile-flag" className="rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />reconcile — sources disagree</Badge>}
      </div>

      {list.length === 0 && !adding && <div className="text-xs text-slate-400">No numbers yet — add the OEM/tag/interchange numbers so this part is searchable.</div>}

      {/* THE SAME LIST AS THE PART PAGE (his call, 23 Oct): under the tag each number came off, part numbers first,
          labelled values below, Copy on each. Add and delete stay here exactly as they were. */}
      {list.length > 0 && (
        <PartNumbersGrouped part={{ ...(part || {}), id: partId, numbers: list }} canManage allowVerify={false}
          onDelete={busy ? undefined : remove} onChanged={onReload} />
      )}

      {adding ? (
        <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50/50">
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Number</label>
              <ScanTextInput testid="number-value" placeholder="Number" value={form.value}
                onChange={(v) => setForm({ ...form, value: v.toUpperCase() })}
                inputClassName="h-11" className="mt-1.5" />
            </div>
            <IdentifyPickField label="Brand" testid="number-brand" addNoun="brand" placeholder="Pick or add a brand"
              value={form.brand} options={brandOptions} canAddNew={canAddBrand}
              onChange={(v) => setForm({ ...form, brand: v })}
              onAddNew={onAddBrand ? (async (name) => await onAddBrand(name)) : (async (name) => name)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.owner} onValueChange={(v) => setForm({ ...form, owner: v })}>
              <SelectTrigger data-testid="number-owner" className="rounded-lg h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{OWNERS.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger data-testid="number-source" className="rounded-lg h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.source === "ai_found_on_source" && <Input data-testid="number-links" placeholder="Source link(s), comma-separated" value={form.source_links} onChange={(e) => setForm({ ...form, source_links: e.target.value })} className="rounded-lg h-9 text-xs" />}
          <div className="flex gap-2">
            <Button data-testid="number-save" size="sm" onClick={add} disabled={busy} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">Add number</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)} className="rounded-lg">Cancel</Button>
          </div>
        </div>
      ) : (
        <Button data-testid="number-add-open" size="sm" variant="outline" onClick={() => setAdding(true)} className="rounded-lg text-xs"><Plus className="w-3.5 h-3.5 mr-1" />Add number</Button>
      )}
    </div>
  );
}
